import React, { useEffect, useRef, useState } from "react";
import { Users2, Loader2, Search, X, Download, FileText, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/**
 * AttendeeDirectory — searchable book of tap-ins. Organiser sees CSV/PDF
 * download buttons + email/mobile columns; everyone else sees public fields.
 */
export function AttendeeDirectory({ eventId, isOwner }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const r = await api.get(`/nfc/events/${eventId}/attendees`,
        { params: query ? { q: query, limit: 200 } : { limit: 200 } });
      setRows(r.data.attendees || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [eventId]);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q), 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const exportUrl = (kind) => `${process.env.REACT_APP_BACKEND_URL || ""}/api/nfc/events/${eventId}/attendees.${kind}`;

  return (
    <section data-testid="event-attendee-directory" className="rounded-3xl bg-white border border-stone-200 overflow-hidden">
      <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 flex items-center gap-2 flex-wrap">
        <Users2 size={13} className="text-stone-500"/>
        <span className="text-[11px] uppercase tracking-widest font-black text-stone-700">Attendee directory · {rows.length}</span>
        {isOwner && (
          <div className="ml-auto flex gap-1.5">
            <a href={exportUrl("csv")} className="text-[11px] font-black rounded-full px-2.5 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1" data-testid="attendee-export-csv" title="Download CSV with contact details">
              <Download size={11}/> CSV
            </a>
            <a href={exportUrl("pdf")} className="text-[11px] font-black rounded-full px-2.5 py-1 border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 inline-flex items-center gap-1" data-testid="attendee-export-pdf" title="Download printable PDF">
              <FileText size={11}/> PDF
            </a>
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search name, role, company, skills, tags, session…"
            className="rounded-full pl-8 text-sm"
            data-testid="attendee-search"
          />
        </div>
      </div>
      {loading ? (
        <div className="p-6 text-center text-sm text-stone-500"><Loader2 className="inline animate-spin mr-1" size={13}/> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-stone-500">
          {q ? `No attendees match "${q}".` : "No tap-ins yet — the directory populates as attendees arrive."}
        </div>
      ) : (
        <ul className="divide-y divide-stone-100 max-h-[560px] overflow-y-auto">
          {rows.map(a => (
            <li key={a.user_id} className="p-3 hover:bg-stone-50" data-testid={`attendee-${a.user_id}`}>
              <button onClick={() => setSelected(a)} className="w-full flex items-center gap-3 text-left">
                {a.user_pic ? (
                  <img src={a.user_pic} alt={a.user_name} className="w-11 h-11 rounded-2xl object-cover shrink-0"/>
                ) : (
                  <div className="w-11 h-11 rounded-2xl bg-fuchsia-50 grid place-items-center text-fuchsia-700 font-black text-sm shrink-0">
                    {(a.user_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 truncate">{a.user_name}</div>
                  <div className="text-[11px] text-stone-500 truncate">
                    {a.title || "—"}{a.company && ` · ${a.company}`}{a.city && ` · ${a.city}`}
                  </div>
                  {(a.skills?.length || a.tags?.length) ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(a.skills || []).slice(0, 3).map((s, i) => (
                        <span key={`s${i}`} className="text-[10px] font-bold bg-emerald-50 text-emerald-800 rounded-full px-2 py-0.5">{s}</span>
                      ))}
                      {(a.tags || []).slice(0, 3).map((t, i) => (
                        <span key={`t${i}`} className="text-[10px] font-bold bg-stone-100 text-stone-600 rounded-full px-2 py-0.5">#{t}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="text-[10px] text-stone-400 font-mono shrink-0">{a.tap_count}×</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected && <AttendeeDetailModal att={selected} onClose={() => setSelected(null)}/>}
    </section>
  );
}

function AttendeeDetailModal({ att: a, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="attendee-detail-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white w-full max-w-md p-5 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-stone-400 hover:text-stone-900"><X size={16}/></button>
        <div className="flex items-center gap-3">
          {a.user_pic ? (
            <img src={a.user_pic} alt={a.user_name} className="w-16 h-16 rounded-2xl object-cover"/>
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-fuchsia-50 grid place-items-center text-fuchsia-700 font-black text-xl">
              {(a.user_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-black text-lg text-stone-900 truncate">{a.user_name}</h3>
            <div className="text-[11px] text-stone-500 truncate">{a.title || "—"}{a.company && ` · ${a.company}`}</div>
            {a.city && <div className="text-[11px] text-stone-500">📍 {a.city}</div>}
          </div>
        </div>
        {a.bio && <p className="text-sm text-stone-600 mt-3">{a.bio}</p>}
        {(a.skills?.length > 0) && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Skills</div>
            <div className="flex flex-wrap gap-1">
              {a.skills.map((s, i) => <span key={i} className="text-[11px] font-bold bg-emerald-50 text-emerald-800 rounded-full px-2 py-0.5">{s}</span>)}
            </div>
          </div>
        )}
        {(a.tags?.length > 0) && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Tags</div>
            <div className="flex flex-wrap gap-1">
              {a.tags.map((s, i) => <span key={i} className="text-[11px] font-bold bg-stone-100 text-stone-700 rounded-full px-2 py-0.5">#{s}</span>)}
            </div>
          </div>
        )}
        {Object.keys(a.socials || {}).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(a.socials).map(([k, v]) => (
              <a key={k} href={v} target="_blank" rel="noreferrer" className="text-[11px] font-black text-emerald-700 hover:underline">
                {k}
              </a>
            ))}
          </div>
        )}
        {a.last_note && <div className="mt-3 text-[11px] text-stone-500">Last note: <em>{a.last_note}</em></div>}
        <div className="mt-4 flex gap-2">
          {a.card_slug && (
            <a href={`/c/${a.card_slug}`} target="_blank" rel="noreferrer" className="flex-1 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-center text-sm font-black py-2 inline-flex items-center justify-center gap-1" data-testid="attendee-open-tapcard">
              Open tap-card <ExternalLink size={12}/>
            </a>
          )}
          <button onClick={onClose} className="rounded-full border border-stone-200 hover:border-stone-400 text-sm font-black px-4 py-2 text-stone-700">Close</button>
        </div>
      </div>
    </div>
  );
}
